import React from 'react';
import { Box, LinearProgress, CircularProgress, Typography } from '@mui/material';
import WidgetWrapper from './WidgetWrapper';

// Helper function to format numbers with decimal and thousands separators
const formatNumber = (value, format) => {
  if (!format || typeof value !== 'number') return value?.toFixed?.(format?.decimals ?? 2) ?? value;

  const { decimals = 2, decimalSeparator = '.', thousandsSeparator = ',' } = format;

  const rounded = Number(value.toFixed(decimals));
  const [intPart, decPart] = rounded.toFixed(decimals).split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  return decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;
};

const ProgressBar = ({
  value = 50,
  min = 0,
  max = 100,
  showValue = true,
  unit = '%',
  color = 'primary',
  height = 20,
  vertical = false,
  orientation = null, // Support 'vertical' or 'horizontal' string format
  circular = false,
  thickness = 4, // For circular progress
  tagId = null,
  tagValue = null, // Will be injected by preview mode
  numberFormat = null, // { decimals, decimalSeparator, thousandsSeparator }
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props
}) => {
  // Support both vertical boolean prop and orientation string prop
  const isVertical = vertical || orientation === 'vertical';

  // Use tag value if available, otherwise use static value
  const currentValue = tagValue !== null && tagValue !== undefined ? tagValue : value;
  const normalizedValue = ((currentValue - min) / (max - min)) * 100;
  const clampedValue = Math.max(0, Math.min(100, normalizedValue));

  // Display value - cap at 100% if exceeds
  const displayValue = formatNumber(Math.min(normalizedValue, 100), numberFormat);

  // Context menu handler - dispatches global event
  const handleContextMenu = (event) => {
    if (tagId) {
      event.preventDefault();
      event.stopPropagation();

      // Dispatch global event to open Tag Info Modal
      const tagInfoData = {
        tagId: tagId,
        tagName: `Progress ${tagId}`,
        tagName2: '',
        currentValue: currentValue,
        unit: unit || ''
      };
      window.dispatchEvent(new CustomEvent('openTagInfo', { detail: tagInfoData }));
    } else {
    }
  };

  // Hooks must be called unconditionally
  const [containerSize, setContainerSize] = React.useState({ width: 100, height: 100 });
  const containerRef = React.useRef(null);
  
  React.useEffect(() => {
    if (!circular) return; // Early return if not circular
    
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    
    // Also update when widget is resized
    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', updateSize);
      resizeObserver.disconnect();
    };
  }, [circular]);

  // Circular progress rendering
  if (circular) {
    const size = Math.min(containerSize.width, containerSize.height) * 0.85;

    return (
      <WidgetWrapper
        backgroundColor={backgroundColor}
        foreColor={foreColor}
        borderWidth={borderWidth}
        borderColor={borderColor}
        borderRadius={borderRadius}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily={fontFamily}
        opacity={opacity}
        onContextMenu={handleContextMenu}
        {...props}
      >
        <Box
          ref={containerRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            position: 'relative'
          }}
        >
          {/* Background circle - only 270 degrees */}
          <CircularProgress
            variant="determinate"
            value={75} // Only show 270 degrees for background
            size={size}
            thickness={thickness}
            sx={{
              position: 'absolute',
              color: 'rgba(0,0,0,0.1)',
              transform: 'rotate(135deg)!important' // Same rotation as progress
            }}
          />
          {/* Progress circle */}
          <CircularProgress
            variant="determinate"
            value={clampedValue * 0.75} // 270 degrees = 75% of full circle
            size={size}
            thickness={thickness}
            sx={{
              color: foreColor || (color === 'primary' ? '#1976d2' : 
                                 color === 'secondary' ? '#dc004e' :
                                 color === 'success' ? '#2e7d32' :
                                 color === 'error' ? '#d32f2f' :
                                 color === 'warning' ? '#ed6c02' :
                                 color === 'info' ? '#0288d1' : '#1976d2'),
              transform: 'rotate(135deg)!important', // Start from left (9 o'clock), end at bottom (6 o'clock)
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round'
              }
            }}
          />
          {showValue && (
            <Typography
              sx={{
                position: 'absolute',
                color: 'inherit',
                fontWeight: 'inherit',
                fontSize: 'inherit'
              }}
            >
              {displayValue}{unit}
            </Typography>
          )}
        </Box>
      </WidgetWrapper>
    );
  }

  // Linear progress rendering
  if (isVertical) {
    // Vertical progress bar - bottom to top
    return (
      <WidgetWrapper
        backgroundColor={backgroundColor}
        foreColor={foreColor}
        borderWidth={borderWidth}
        borderColor={borderColor}
        borderRadius={borderRadius}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily={fontFamily}
        opacity={opacity}
        onContextMenu={handleContextMenu}
        {...props}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column-reverse',
            alignItems: 'center',
            justifyContent: 'flex-start',
            backgroundColor: 'rgba(0,0,0,0.1)'
          }}
        >
          {/* Progress fill - grows from bottom */}
          <Box
            sx={{
              width: '100%',
              height: `${clampedValue}%`,
              backgroundColor: foreColor || (color === 'primary' ? '#1976d2' :
                                         color === 'secondary' ? '#dc004e' :
                                         color === 'success' ? '#2e7d32' :
                                         color === 'error' ? '#d32f2f' :
                                         color === 'warning' ? '#ed6c02' :
                                         color === 'info' ? '#0288d1' : '#1976d2'),
              transition: 'height 0.3s ease'
            }}
          />
          {/* Value text */}
          {showValue && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: clampedValue > 50 ? 'white' : 'black',
                fontWeight: 'bold',
                fontSize: 'inherit',
                whiteSpace: 'nowrap',
                textShadow: clampedValue > 50 ? '0 0 2px rgba(0,0,0,0.5)' : '0 0 2px rgba(255,255,255,0.8)',
                zIndex: 1
              }}
            >
              {displayValue}{unit}
            </Typography>
          )}
        </Box>
      </WidgetWrapper>
    );
  }

  // Horizontal progress bar
  return (
    <WidgetWrapper
      backgroundColor={backgroundColor}
      foreColor={foreColor}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      onContextMenu={handleContextMenu}
      {...props}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <LinearProgress
          variant="determinate"
          value={clampedValue}
          sx={{
            height: '100%',
            width: '100%',
            borderRadius: 0,
            backgroundColor: 'rgba(0,0,0,0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: foreColor || (color === 'primary' ? '#1976d2' :
                                         color === 'secondary' ? '#dc004e' :
                                         color === 'success' ? '#2e7d32' :
                                         color === 'error' ? '#d32f2f' :
                                         color === 'warning' ? '#ed6c02' :
                                         color === 'info' ? '#0288d1' : '#1976d2')
            }
          }}
        />
        {showValue && (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: clampedValue > 50 ? 'white' : 'black',
              fontWeight: 'bold',
              fontSize: 'inherit',
              whiteSpace: 'nowrap',
              textShadow: clampedValue > 50 ? '0 0 2px rgba(0,0,0,0.5)' : '0 0 2px rgba(255,255,255,0.8)'
            }}
          >
            {displayValue}{unit}
          </Typography>
        )}
      </Box>
    </WidgetWrapper>
  );
};

export default ProgressBar;